import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import IconButtonTsoPq from '@components/Buttons/IconButtonTsoPq';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { Box, styled, Typography } from '@mui/material';
import { setSnackbar } from '@redux/slices/snackbarSlice';
import { DragEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

type DropzoneTsoPqProps = {
  onFilesAccepted: (files: File[]) => void;
};

const StyledDropzone = styled(Box, { shouldForwardProp: (prop) => prop !== 'isDragging' })(({
  isDragging,
}: {
  isDragging: boolean;
}) => {
  return {
    padding: '0.75rem 2.875rem',
    textAlign: 'center',
    borderRadius: tsoPqTheme.shape.border_radius_tso_border_radius_4,
    border: '1px dashed',
    borderColor: isDragging
      ? 'primary.main'
      : tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_enabled,
    backgroundColor: isDragging ? 'grey.100' : tsoPqTheme.palette.background.primary.color_primary_background_bg_prim,
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    position: 'relative',
  };
});

const StyledIconButtonTsoPq = styled(IconButtonTsoPq)(() => {
  return {
    display: 'flex',
    margin: 'auto',
  };
});

const StyledDropzoneContent = styled(Box)(() => {
  return {
    width: '43.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_12,
    justifySelf: 'center',
  };
});

const StyledDescription = styled(Typography)(() => {
  return {
    color: tsoPqTheme.palette.customTextColor.color_primary_font_ft_prim_enabled,
    fontFamily: tsoPqTheme.typography.openSans,
    fontWeight: 500,
    fontSize: tsoPqTheme.typography.fontsize_support_support_text_small,
    lineHeight: tsoPqTheme.typography.lineHeight_m,
    textAlign: 'center',
    verticalAlign: 'middle',
  };
});

const DropzoneTsoPq = ({ onFilesAccepted }: DropzoneTsoPqProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const files = Array.from(event.dataTransfer.files);
    const csvFiles = files.filter((file) => file.name.toLowerCase().endsWith('.csv'));

    if (csvFiles.length > 0) {
      onFilesAccepted(csvFiles);
    } else {
      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: t('upload.fileUpload.uploadingFile.typeError'),
        })
      );
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    const csvFiles = files.filter((file) => file.name.toLowerCase().endsWith('.csv'));
    if (csvFiles.length > 0) {
      onFilesAccepted(csvFiles);
    } else {
      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: t('upload.fileUpload.uploadingFile.typeError'),
        })
      );
    }
  };

  return (
    <StyledDropzone
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      isDragging={isDragging}
      onClick={() => document.getElementById('fileInput')?.click()}
    >
      <StyledDropzoneContent>
        <StyledIconButtonTsoPq sizeVariant={'l'} icon={faPlus} buttonType={'filledPrimary'} />
        <input id="fileInput" type="file" multiple hidden onChange={handleFileInputChange} accept=".csv" />
        <StyledDescription>{isDragging ? t('dropzone.drag') : t('dropzone.description')}</StyledDescription>
      </StyledDropzoneContent>
    </StyledDropzone>
  );
};

export default DropzoneTsoPq;
